<template>

  <!-- Loading State -->
  <v-container v-if="loading">
    <v-row>
      <v-col cols="12" class="text-center">
        <v-progress-circular 
        indeterminate 
        color="primary"
        size="64"
      ></v-progress-circular>
      </v-col>
    </v-row>
  </v-container>

  <!-- Error State -->
  <v-container v-else-if="error">
    <v-row>
      <v-col cols="12" class="text-center error">
        {{ $t('startEvaluation.error') }}: {{ error }}
      </v-col>
    </v-row>
  </v-container>

  <!-- Success State - Evaluation Loaded -->
  <v-container v-else-if="evaluation" >
    <v-row>
      <v-col cols="12" class="text-center">
        <h1 class="text-h5 mb-4">{{ $t('startEvaluation.ready_for_evaluation') }}</h1>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="d-flex justify-center">
        <v-card elevation="1">
          <v-card-text>
              <v-table class="spaced-table">
                <tr>
                  <th class="text-left ">{{ $t('startEvaluation.created_by') }}</th>
                  <td class="text-left">{{ evaluation?.psychologist?.first_name}} {{ evaluation?.psychologist?.last_name}}</td>
                </tr>
                <tr>
                  <th class="text-left">{{ $t('startEvaluation.created_for') }}</th>
                  <td class="text-left">{{ evaluation?.client?.first_name}} {{ evaluation?.client?.last_name[0]}}.</td>
                </tr>
                <tr>
                  <th class="text-left">{{ $t('startEvaluation.expires_on') }}</th>
                  <td class="text-left">
                    {{ formatDate(evaluation?.expiresOn)}}
                    ({{ formatTime(evaluation?.expiresOn)}})
                  </td>
                </tr>
                <tr>
                  <th class="text-left">{{ $t('startEvaluation.aproximate_time_to_take_it') }}</th>
                  <td class="text-left" >
                    {{ timeToComplete.min }} - {{ timeToComplete.max }} {{ $t('startEvaluation.minutes') }}
                  </td>
                </tr>
              </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="text-center">
        <div v-if="evaluation.status == 'fresh'">
          <div v-if="isExpired">
            {{ $t('startEvaluation.evaluation_expired') }}
          </div>
          <div v-else>
            <v-card color="warning" variant="tonal" class="mx-auto" max-width="400">
              <v-card-text class="d-flex align-center">
                <v-icon icon="mdi-alert" class="me-3" color="warning"></v-icon>
                <div>
                  <div class="text-subtitle-1 font-weight-medium mb-1">
                    {{ $t('startEvaluation.warning') }}
                  </div>
                  <div class="text-body-2">
                    {{ $t('startEvaluation.please_be_aware_that_the_moment_you_start') }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
            <v-btn @click="onStart" color="primary" size="large" class="mt-4">{{ $t('startEvaluation.start_evaluation') }}</v-btn>
          </div>
        </div>
        <div v-else-if="evaluation.status == 'started'">
          <v-card max-width="400" class="mx-auto">
            <v-card-text class="text-center">
              <div class="text-subtitle-1 mb-2">{{ $t('startEvaluation.evaluation_already_started') }}</div>
              <v-btn v-if="checkBound()" @click="onContinue" color="primary">
                {{ $t('startEvaluation.continue') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </div>
        <div v-else-if="evaluation.status == 'completed'">
          {{ $t('startEvaluation.evaluation_already_completed') }}
        </div>
      </v-col>
    </v-row>

    <v-row v-if="error">
      <v-col cols="12" class="text-center text-error">
        {{ $t('startEvaluation.error') }}: {{ error }}
      </v-col>
    </v-row>

  </v-container>

  <!-- Fallback - No Data -->
  <v-container v-else>
    <v-row>
      <v-col cols="12" class="text-center warning">
        {{ $t('startEvaluation.no_evaluation_found') }}
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiService from '@/services/ApiService'
import {formatDate, formatTime} from '@/utils/time.js'

export default {
  name: 'TakeEvaluationView',
  data() {
    return {
      loading: true,
      token: '',
      evaluation: null,
      error: null,
    }
  },

  computed:{
    isExpired(){
      let createdDate = new Date(this.evaluation.createdDate);
      let expirindDate = new Date(this.evaluation.expiresOn);
      let currentDate = new Date();
      if(currentDate > expirindDate) return true
      return false;
    },

    timeToComplete(){      
      let min = 0, max = 0;
      for(let idx = 0; idx < this.evaluation.scales.length; idx++){
        let evaluation = this.evaluation.scales[idx]
        min += evaluation.min_completion_time
        max += evaluation.max_completion_time
      }
      return {
        min: min,
        max: max
      }
    }
    
  },

  async mounted() {      
      await this.loadEvaluation()
  },

  methods: {

    formatDate,

    formatTime,

    async loadEvaluation(){
      this.token = this.$route.params.token
      console.log('Evaluation token:', this.token)

      try {
        this.loading = true
        let response = await apiService.getPublicEvaluationByToken(this.token)
        console.log('Response:', response);

        this.evaluation = response.data;

        //Set language for evaluation based on evaluation language
        this.$i18n.locale = this.evaluation.language || 'en';
      } catch (error) {
        console.error('Error fetching data:', error.message);
        this.error = error.message
      } finally {
        this.loading = false;
      }
    },

    async onStart() {
      this.error = null;
      console.log(`TakeEvaluationView > onStart....`)

      //1. get fingerprint from server
      let fingerprint = await this.getFingerPrint();
      if(fingerprint){        
        //2. store fingerprint
        if(this.evaluation && this.evaluation.token){
          // Store binding confirmation locally (for user feedback)
          localStorage.setItem(`bound_${this.evaluation.token}`, fingerprint);

          //3. redirect to questions          
          this.$router.push(`/evaluation/${this.token}/questionnaire`);
        }
      }      
    },

    async getFingerPrint(){      
      try{
        //1. generate fingerprint 
        //either: 1 generate it locally and inform server or 2. get it from server
        let response = await apiService.getEvaluationClientFingerByToken(this.evaluation.token);
        console.log('TakeEvaluationView > TakeEvaluationView > onStart > response:', response);

        if(response.success){
          return response.data.device_fingerprint;
        }
        
      }catch(err){
        console.error(`TakeEvaluationView > TakeEvaluationView > error: ${ err.message}`)
        this.error = this.$t('startEvaluation.could_not_get_the_fingerprint_from_server')
      }
      return '';
    },

    checkBound(){
      let fingerprint = localStorage.getItem(`bound_${this.evaluation.token}`);
      return fingerprint == this.evaluation.device_fingerprint;
    },

    onContinue(){
      this.$router.push(`/evaluation/${this.token}/questionnaire`);
    }
  }

}
</script>

<style scoped>
.spaced-table td {
  padding-left: 12px;  
}
</style>